import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CarFrontFill, Tag, CheckCircle, Calendar3, BroadcastPin, ArrowRight } from "react-bootstrap-icons";
import PageHero from "../../common/ui/PageHero";
import { LoadingBlock, EmptyBlock, ErrorBlock } from "../../common/ui/StateBlock";
import { formatPrice, formatUsdFromGel, formatDateTime, localizeEventTitle } from "../../common/format";
import { currentLanguage } from "../../common/ui/notify";
import { t } from "./auctionI18n";
import "./Auction.css";
import "../../common/ui/ui.css";

const statusLabel = (status) => ({
    scheduled: t("დაგეგმილი", "Scheduled", "Запланирован", "Նախատեսված"),
    live: t("მიმდინარე", "Live", "В эфире", "Ուղիղ"),
    paused: t("შეჩერებული", "Paused", "Приостановлен", "Դադարեցված"),
    finished: t("დასრულებული", "Finished", "Завершён", "Ավարտված"),
}[status] || status);

// Short "starts in" / "live" helper for the card meta line.
function startsInLabel(ev, now) {
    if (ev.status === "live") return t("ახლა ეთერშია", "Live now", "Сейчас в эфире", "Հիմա ուղիղ");
    if (ev.status === "paused") return t("შეჩერებულია", "Paused", "Приостановлен", "Դադարեցված");
    if (ev.status === "finished") return t("დასრულდა", "Finished", "Завершён", "Ավարտվեց");
    if (!ev.starts_at) return null;
    const ms = new Date(ev.starts_at).getTime() - now;
    if (ms <= 0) return t("იწყება", "Starting", "Начинается", "Սկսվում է");
    const mins = Math.floor(ms / 60000);
    const days = Math.floor(mins / 1440);
    const hours = Math.floor((mins % 1440) / 60);
    if (days > 0) return t(`${days} დღეში`, `in ${days}d`, `через ${days} дн.`, `${days} օրից`);
    if (hours > 0) return t(`${hours} სთ ${mins % 60} წთ-ში`, `in ${hours}h ${mins % 60}m`, `через ${hours} ч ${mins % 60} мин`, `${hours}ժ ${mins % 60}ր-ից`);
    return t(`${mins} წთ-ში`, `in ${mins}m`, `через ${mins} мин`, `${mins}ր-ից`);
}

const priceRange = (ev) => {
    if (ev.min_price == null) return null;
    const cur = ev.currency || "GEL";
    if (ev.max_price == null || Number(ev.min_price) === Number(ev.max_price)) return formatPrice(ev.min_price, cur);
    return `${formatPrice(ev.min_price, cur)} – ${formatPrice(ev.max_price, cur)}`;
};

// The same range in USD, using the event's locked NBG rate.
const priceRangeUsd = (ev) => {
    if (ev.min_price == null || !ev.usd_rate) return null;
    const lo = formatUsdFromGel(ev.min_price, ev.usd_rate);
    if (!lo) return null;
    if (ev.max_price == null || Number(ev.min_price) === Number(ev.max_price)) return lo;
    const hi = formatUsdFromGel(ev.max_price, ev.usd_rate);
    return hi ? `${lo} – ${hi}` : lo;
};

const AuctionList = () => {
    const lang = currentLanguage();
    const [events, setEvents] = useState([]);
    const [state, setState] = useState("loading");
    const [now, setNow] = useState(Date.now());

    const load = () => {
        setState("loading");
        axios.get("/api/auction/events")
            .then((res) => { setEvents(res.data.events || []); setState("ready"); })
            .catch(() => setState("error"));
    };
    useEffect(load, []);
    useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);

    return (
        <>
            <PageHero eyebrow="Carexpress"
                title={t("ლაივ აუქციონები", "Live auctions", "Живые аукционы", "Կենդանի աճուրդներ")} accent=""
                subtitle={t("დაათვალიერეთ დაგეგმილი და მიმდინარე აუქციონები.", "Browse scheduled and ongoing auctions.", "Просмотрите запланированные и текущие аукционы.", "Դիտեք նախատեսված և ընթացիկ աճուրդները։")} />
            <div className="container auction-list">
                {state === "loading" && <LoadingBlock label={t("იტვირთება...", "Loading...", "Загрузка...", "Բեռնվում է...")} />}
                {state === "error" && <ErrorBlock title={t("შეცდომა", "Error", "Ошибка", "Սխալ")} message={t("აუქციონების ჩატვირთვა ვერ მოხერხდა.", "Could not load auctions.", "Не удалось загрузить аукционы.", "Չհաջողվեց բեռնել աճուրդները։")} retryLabel={t("თავიდან", "Retry", "Повторить", "Կրկին")} onRetry={load} />}
                {state === "ready" && events.length === 0 && <EmptyBlock title={t("აუქციონები არ არის", "No auctions", "Нет аукционов", "Աճուրդներ չկան")} message={t("ამჟამად დაგეგმილი აუქციონი არ არის.", "There are no scheduled auctions right now.", "Сейчас нет запланированных аукционов.", "Այս պահին նախատեսված աճուրդ չկա։")} />}
                {state === "ready" && events.length > 0 && (
                    <div className="auction-event-grid">
                        {events.map((ev) => {
                            const previews = Array.isArray(ev.previews) ? ev.previews : [];
                            const cover = previews[0];
                            const isLive = ev.status === "live" || ev.status === "paused";
                            const range = priceRange(ev);
                            const rangeUsd = priceRangeUsd(ev);
                            return (
                                <Link to={`/auction/event/${ev.id}`} key={ev.id} className="cx-card auction-ev-card">
                                    <div className="auction-ev-cover" style={cover ? { backgroundImage: `url('${cover}')` } : {}}>
                                        {!cover && <CarFrontFill className="auction-ev-cover-ph" />}
                                        <span className={`auction-chip auction-chip-${ev.status} auction-ev-status`}>
                                            {ev.status === "live" && <BroadcastPin />} {statusLabel(ev.status)}
                                        </span>
                                        {previews.length > 1 && (
                                            <div className="auction-ev-strip">
                                                {previews.slice(1, 5).map((img, i) => (
                                                    <span key={i} className="auction-ev-stripimg" style={{ backgroundImage: `url('${img}')` }} />
                                                ))}
                                                {ev.lot_count > previews.length && <span className="auction-ev-stripmore">+{ev.lot_count - previews.length}</span>}
                                            </div>
                                        )}
                                    </div>

                                    <div className="auction-ev-body">
                                        <h3>{localizeEventTitle(ev.title, lang) || t(`აუქციონი #${ev.id}`, `Auction #${ev.id}`, `Аукцион #${ev.id}`, `Աճուրդ #${ev.id}`)}</h3>

                                        <div className="auction-ev-meta">
                                            <span className={`auction-ev-when ${isLive ? "is-live" : ""}`}>
                                                {isLive ? <BroadcastPin /> : <Calendar3 />} {startsInLabel(ev, now)}
                                            </span>
                                            <span className="auction-muted auction-ev-date">{formatDateTime(ev.starts_at)}</span>
                                        </div>

                                        {ev.live_lot && (
                                            <div className="auction-ev-nowlot">
                                                <span className="auction-ev-nowdot" />
                                                {t("ახლა:", "Now:", "Сейчас:", "Հիմա:")} <strong>{[ev.live_lot.year, ev.live_lot.mark, ev.live_lot.model].filter(Boolean).join(" ")}</strong>
                                                <span className="auction-ev-nowprice">{formatPrice(ev.live_lot.current_price, ev.currency || "GEL")}{ev.usd_rate && formatUsdFromGel(ev.live_lot.current_price, ev.usd_rate) ? <span className="auction-usd">≈ {formatUsdFromGel(ev.live_lot.current_price, ev.usd_rate)}</span> : null}</span>
                                            </div>
                                        )}

                                        {Array.isArray(ev.makes) && ev.makes.length > 0 && (
                                            <div className="auction-ev-makes">
                                                {ev.makes.map((m) => <span key={m} className="auction-ev-make">{m}</span>)}
                                            </div>
                                        )}

                                        <div className="auction-ev-stats">
                                            <div className="auction-ev-stat"><CarFrontFill /> <span>{t(`${ev.lot_count} ლოტი`, `${ev.lot_count} lots`, `${ev.lot_count} лотов`, `${ev.lot_count} լոտ`)}</span></div>
                                            {ev.sold_count > 0 && <div className="auction-ev-stat"><CheckCircle /> <span>{t(`${ev.sold_count} გაყიდული`, `${ev.sold_count} sold`, `${ev.sold_count} продано`, `${ev.sold_count} վաճառված`)}</span></div>}
                                            {range && <div className="auction-ev-stat"><Tag /> <span>{range}{rangeUsd ? <span className="auction-usd">· {rangeUsd}</span> : null}</span></div>}
                                        </div>

                                        <span className="auction-ev-cta">{t("დათვალიერება", "View", "Смотреть", "Դիտել")} <ArrowRight /></span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default AuctionList;
